import { useCallback, useEffect, useState } from "react";

export type Appearance = "system" | "light" | "dark";
export type Palette = "standard" | "color-vision";
export type ResolvedAppearance = "light" | "dark";

interface DisplaySettingsState {
  appearance: Appearance;
  palette: Palette;
  resolvedAppearance: ResolvedAppearance;
}

const APPEARANCE_KEY = "riskwatch.appearance";
const PALETTE_KEY = "riskwatch.palette";

const validAppearances = new Set<Appearance>(["system", "light", "dark"]);
const validPalettes = new Set<Palette>(["standard", "color-vision"]);

function systemAppearance(): ResolvedAppearance {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function readAppearance(): Appearance {
  if (typeof window === "undefined") return "system";
  const value = window.localStorage.getItem(APPEARANCE_KEY) as Appearance | null;
  return value && validAppearances.has(value) ? value : "system";
}

function readPalette(): Palette {
  if (typeof window === "undefined") return "standard";
  const value = window.localStorage.getItem(PALETTE_KEY) as Palette | null;
  return value && validPalettes.has(value) ? value : "standard";
}

function resolveAppearance(appearance: Appearance): ResolvedAppearance {
  return appearance === "system" ? systemAppearance() : appearance;
}

function applyDisplaySettings(state: DisplaySettingsState) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.dataset.theme = state.resolvedAppearance;
  root.dataset.palette = state.palette;
  root.classList.toggle("dark", state.resolvedAppearance === "dark");
  root.style.colorScheme = state.resolvedAppearance;
}

function initialState(): DisplaySettingsState {
  const appearance = readAppearance();
  const palette = readPalette();
  return {
    appearance,
    palette,
    resolvedAppearance: resolveAppearance(appearance),
  };
}

const store = {
  state: initialState(),
  subs: new Set<() => void>(),
  set(patch: Partial<Pick<DisplaySettingsState, "appearance" | "palette">>) {
    const nextAppearance = patch.appearance ?? store.state.appearance;
    const nextPalette = patch.palette ?? store.state.palette;
    store.state = {
      appearance: nextAppearance,
      palette: nextPalette,
      resolvedAppearance: resolveAppearance(nextAppearance),
    };
    if (typeof window !== "undefined") {
      window.localStorage.setItem(APPEARANCE_KEY, store.state.appearance);
      window.localStorage.setItem(PALETTE_KEY, store.state.palette);
    }
    applyDisplaySettings(store.state);
    for (const subscriber of store.subs) subscriber();
  },
  refreshSystem() {
    if (store.state.appearance !== "system") return;
    const resolvedAppearance = systemAppearance();
    if (resolvedAppearance === store.state.resolvedAppearance) return;
    store.state = { ...store.state, resolvedAppearance };
    applyDisplaySettings(store.state);
    for (const subscriber of store.subs) subscriber();
  },
};

applyDisplaySettings(store.state);

export function useDisplaySettings() {
  const [, force] = useState(0);

  useEffect(() => {
    const subscriber = () => force((value) => value + 1);
    store.subs.add(subscriber);
    applyDisplaySettings(store.state);

    const media = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => store.refreshSystem();
    media.addEventListener("change", onChange);
    return () => {
      store.subs.delete(subscriber);
      media.removeEventListener("change", onChange);
    };
  }, []);

  const setAppearance = useCallback((appearance: Appearance) => store.set({ appearance }), []);
  const setPalette = useCallback((palette: Palette) => store.set({ palette }), []);

  return {
    appearance: store.state.appearance,
    palette: store.state.palette,
    resolvedAppearance: store.state.resolvedAppearance,
    setAppearance,
    setPalette,
  };
}
